import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { Users, Plus, Building2, Phone, Mail, TrendingUp, CheckCircle, Clock, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function BrokerDashboard() {
  const { API } = useAuth();
  const [listings, setListings] = useState([]);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [showLead, setShowLead] = useState(false);
  const [form, setForm] = useState({ property_id: "", buyer_name: "", buyer_phone: "", buyer_email: "", budget: "", notes: "" });

  const fetchData = useCallback(async () => {
    try {
      const [lRes, ldRes, sRes] = await Promise.all([
        fetch(`${API}/broker/listings`, { credentials: "include" }),
        fetch(`${API}/broker/leads`, { credentials: "include" }),
        fetch(`${API}/broker/stats`, { credentials: "include" }),
      ]);
      if (lRes.ok) setListings(await lRes.json());
      if (ldRes.ok) setLeads(await ldRes.json());
      if (sRes.ok) setStats(await sRes.json());
    } catch (e) { console.error(e); }
  }, [API]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createLead = async () => {
    try {
      await fetch(`${API}/broker/leads`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(form),
      });
      setShowLead(false);
      setForm({ property_id: "", buyer_name: "", buyer_phone: "", buyer_email: "", budget: "", notes: "" });
      fetchData();
    } catch (e) { console.error(e); }
  };

  const updateLeadStatus = async (leadId, status) => {
    try {
      await fetch(`${API}/broker/leads/${leadId}`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ status }),
      });
      fetchData();
    } catch (e) { console.error(e); }
  };

  const statusColors = {
    active: { bg: "bg-[#D4AF37]/10", text: "text-[#D4AF37]" },
    converted: { bg: "bg-[#556B2F]/10", text: "text-[#556B2F]" },
    lost: { bg: "bg-red-400/10", text: "text-red-400" },
  };

  return (
    <div data-testid="broker-dashboard">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif text-2xl text-white">Broker Console</h2>
          <p className="text-white/40 text-sm font-sans">Manage listings, leads, and transactions</p>
        </div>
        <Button data-testid="add-lead-btn" onClick={() => setShowLead(true)} className="bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm font-sans">
          <Plus className="w-4 h-4 mr-2" /> New Lead
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {[
            { label: "Available Properties", value: stats.available_properties, color: "#D4AF37" },
            { label: "Total Leads", value: stats.total_leads, color: "#F59E0B" },
            { label: "Active Leads", value: stats.active_leads, color: "#556B2F" },
            { label: "Converted", value: stats.converted, color: "#D4AF37" },
          ].map(s => (
            <div key={s.label} className="bg-[#11130F] border border-white/10 rounded-sm p-4">
              <div className="w-2 h-2 rounded-full mb-2" style={{ backgroundColor: s.color }} />
              <p className="font-serif text-2xl text-white">{s.value}</p>
              <p className="text-white/40 text-[10px] font-sans uppercase tracking-wider">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 bg-[#11130F] border border-white/10 rounded-sm p-1 w-fit">
        {[{ key: "overview", label: "Available Listings" }, { key: "leads", label: "My Leads" }].map(t => (
          <button key={t.key} data-testid={`broker-tab-${t.key}`} onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 text-xs font-sans rounded-sm transition-colors ${activeTab === t.key ? "bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20" : "text-white/50 hover:text-white"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === "overview" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {listings.length === 0 ? (
            <div className="col-span-full bg-[#11130F] border border-white/10 rounded-sm p-12 text-center">
              <Building2 className="w-12 h-12 text-white/10 mx-auto mb-4" />
              <p className="text-white/40 text-sm font-sans">No properties available for brokerage at this time.</p>
            </div>
          ) : listings.map(prop => (
            <div key={prop.property_id} className="bg-[#11130F] border border-white/10 rounded-sm p-5 hover:border-[#D4AF37]/40 transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="w-4 h-4 text-[#D4AF37]" />
                <h4 className="font-serif text-base text-white">{prop.name}</h4>
              </div>
              <p className="text-white/50 text-xs font-sans mb-3">{prop.address}</p>
              <div className="flex items-center justify-between text-xs font-sans mb-1">
                <span className="text-white/40">Valuation</span>
                <span className="text-white font-medium">${prop.valuation?.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-xs font-sans mb-3">
                <span className="text-white/40">Area</span>
                <span className="text-white">{prop.area_sqft?.toLocaleString()} sqft</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {(prop.badges || []).slice(0, 3).map(b => (
                  <span key={b} className="text-[9px] px-1.5 py-0.5 rounded-sm bg-[#D4AF37]/10 text-[#D4AF37] font-sans">{b.replace(/_/g, " ")}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === "leads" && (
        <div className="space-y-3">
          {leads.length === 0 ? (
            <div className="bg-[#11130F] border border-white/10 rounded-sm p-12 text-center">
              <Users className="w-12 h-12 text-white/10 mx-auto mb-4" />
              <p className="text-white/40 text-sm font-sans">No leads registered yet. Add buyer leads to track them.</p>
            </div>
          ) : leads.map(lead => {
            const sc = statusColors[lead.status] || statusColors.active;
            return (
              <div key={lead.lead_id} className="bg-[#11130F] border border-white/10 rounded-sm p-5 hover:border-[#D4AF37]/40 transition-colors">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-sans font-medium text-white text-sm">{lead.buyer_name}</h4>
                    <div className="flex items-center gap-3 mt-1">
                      {lead.buyer_phone && <span className="text-white/40 text-[10px] font-sans flex items-center gap-1"><Phone className="w-3 h-3" />{lead.buyer_phone}</span>}
                      {lead.buyer_email && <span className="text-white/40 text-[10px] font-sans flex items-center gap-1"><Mail className="w-3 h-3" />{lead.buyer_email}</span>}
                    </div>
                    {lead.budget && <p className="text-white/50 text-xs font-sans mt-1">Budget: {lead.budget}</p>}
                    {lead.notes && <p className="text-white/30 text-xs font-sans mt-1">{lead.notes}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`${sc.bg} ${sc.text} text-[10px] px-2 py-0.5 rounded-sm font-sans capitalize`}>{lead.status}</span>
                    {lead.status === "active" && (
                      <div className="flex gap-1">
                        <button onClick={() => updateLeadStatus(lead.lead_id, "converted")} className="text-[#556B2F] hover:text-[#D4AF37] transition-colors" title="Mark Converted">
                          <CheckCircle className="w-4 h-4" />
                        </button>
                        <button onClick={() => updateLeadStatus(lead.lead_id, "lost")} className="text-white/20 hover:text-red-400 transition-colors" title="Mark Lost">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={showLead} onOpenChange={setShowLead}>
        <DialogContent className="bg-[#11130F] border border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-xl">Register New Lead</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">Buyer Name</label>
              <Input data-testid="lead-name" value={form.buyer_name} onChange={e => setForm({ ...form, buyer_name: e.target.value })} placeholder="Full name" className="bg-white/5 border-white/10 text-white rounded-sm" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-white/50 text-xs font-sans mb-1 block">Phone</label>
                <Input value={form.buyer_phone} onChange={e => setForm({ ...form, buyer_phone: e.target.value })} placeholder="+91..." className="bg-white/5 border-white/10 text-white rounded-sm" />
              </div>
              <div>
                <label className="text-white/50 text-xs font-sans mb-1 block">Email</label>
                <Input value={form.buyer_email} onChange={e => setForm({ ...form, buyer_email: e.target.value })} placeholder="email@..." className="bg-white/5 border-white/10 text-white rounded-sm" />
              </div>
            </div>
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">Budget</label>
              <Input value={form.budget} onChange={e => setForm({ ...form, budget: e.target.value })} placeholder="e.g., 50L - 1Cr" className="bg-white/5 border-white/10 text-white rounded-sm" />
            </div>
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">Property Interest</label>
              <Select value={form.property_id} onValueChange={v => setForm({ ...form, property_id: v })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white rounded-sm"><SelectValue placeholder="Select property (optional)" /></SelectTrigger>
                <SelectContent className="bg-[#11130F] border-white/10">
                  {listings.map(p => <SelectItem key={p.property_id} value={p.property_id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">Notes</label>
              <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Requirements, preferences..." className="bg-white/5 border-white/10 text-white rounded-sm" />
            </div>
            <Button data-testid="submit-lead-btn" onClick={createLead} disabled={!form.buyer_name} className="w-full bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm disabled:opacity-50">Register Lead</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
